import React, { useState, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import * as ResultModule from 'modules/result';
import { answerText, hasAnswer } from 'components/result/AnswerReviewBody';
import { AnswerReviewList } from 'components/result/AnswerReviewList';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'modules/auth/context';
import { estimateCategoryScore } from 'modules/test/category-scoring';
import { ScaledReportCard } from 'modules/standardizedExam/components/report/ScaledReportCard';
import { ShareScoreButton } from 'modules/standardizedExam/components/share/ShareScoreButton';
import type { SpeakingClip } from 'modules/standardizedExam/components/report/SpeakingClips';
import { SessionReview } from 'pages/Portal/SessionReview';
import { ExamInsightsProvider, InsightsOpenBanner, InsightsUnlockBanner, useExamInsightsUnlock } from 'modules/examInsights';
import { useSessionReview } from 'modules/studyPlan';
import { FeedbackCard } from 'modules/feedback';
import { WeakTopics } from 'components/result/WeakTopics';
import { shouldShowWeakTopics, topicBreakdown, weakTopics } from 'components/result/topicBreakdown';
import { CategoryResultHero, ResultInsights, ResultPageHeader, SkillAnalysis, buildCategoryResult, AttemptHistory, attemptDelta, dtmPoints, testBall, resultCategoryKey, RESULT_ACCENTS, type AnalysisAnswer } from 'modules/resultView';

const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
};

const formatDate = (d: Date) =>
    new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

type AnswerItem = {
    questionId: string;
    questionText: string;
    questionType: string;
    sectionId: string;
    sectionTitle: string;
    answer: string | string[];
    correctAnswer?: string | string[];
    isCorrect: boolean;
    pointsEarned: number;
    maxPoints: number;
    timeSpent: number;
    flagged?: boolean;
    options:
        | Array<{ id: string; value: string; isCorrect?: boolean; isSelected: boolean }>
        | null;
    matchPairs: Array<{ key: string; correctValue?: string; selectedValue: string }> | null;
    answerRaw?: unknown;
    correctAnswerRaw?: unknown;
    questionData?: Record<string, any> | null;
    explanation?: string;
    aiReasoning?: string;
    aiAnalysis?: { available: boolean; texts: Record<string, string> };
    aiSubAnalysis?: Record<string, { available: boolean; texts: Record<string, string>; locked?: boolean }>;
    /** Items right out of items asked: a passage counts each sub-question, so Reading reads 28/40, not 0/1. */
    itemsCorrect: number;
    itemsTotal: number;
    gradingPending?: boolean;
    speakingRecordings?: SpeakingClip[];
};

const TestResults: React.FC = () => {
    const { t } = useTranslation();
    const { state: authState } = useAuthContext();
    const { id } = useParams<{ id: string }>();
    const { pathname } = useLocation();
    // Reused by both shells: staff at /app/results/:id (back to History) and
    // candidates at /portal/results/:id (back to their portal Results list).
    const isPortal = pathname.startsWith('/portal');
    const backTo = isPortal ? '/portal/results' : '/app/history';
    const backLabel = isPortal ? t('portal.back_to_results') : t('back_to_history');
    const [currentIndex, setCurrentIndex] = useState(0);
    const { item, isLoading, isError, error, refetch, isFetching } = ResultModule.Hooks.useSingle({
        id: id ?? '',
        enabled: !!id,
    });
    // The candidate's own study review already lists their weak topics when it has any; this is the same query it makes.
    const { data: studyReview, isLoading: studyReviewLoading } = useSessionReview(id, isPortal);
    // IELTS/SAT on the marketplace: the score is free, the full analysis unlocks with a plan credit.
    const insightsUnlock = useExamInsightsUnlock(id, item?.insights?.locked === true, () => void refetch());

    const weak = useMemo(() => weakTopics(topicBreakdown((item?.answers ?? []) as AnswerItem[])), [item]);

    const goalLevel = useMemo(() => {
        const cat = (item?.test?.category || '').toLowerCase();
        return authState?.user?.learnerProfile?.goals?.find((g) => {
            if (cat.includes('milliy') && g.exam === 'MILLIY_SERTIFIKAT') return true;
            if (cat === 'dtm' && g.exam === 'DTM') return true;
            if (cat === 'ielts' && g.exam === 'IELTS') return true;
            if (cat === 'sat' && g.exam === 'SAT') return true;
            if ((cat === 'attestation' || cat === 'attestatsiya') && g.exam === 'ATTESTATSIYA') return true;
            return false;
        })?.level;
    }, [item?.test?.category, authState?.user?.learnerProfile?.goals]);

    const categoryEstimate = useMemo(() => {
        if (!item) return null;
        const testObj = item.test;
        return estimateCategoryScore({
            category: testObj?.category,
            percentage: item.percentage ?? 0,
            title: testObj?.title,
            tags: testObj?.tags,
            subjectConfigsCount: testObj?.subjectConfigs?.length,
            scaledReport: item.scaledReport,
            userGoalLevel: goalLevel,
        });
    }, [item, goalLevel]);

    if (!id) {
        return (
            <div className="ts-shell p-6">
                <div className="space-y-6">
                    <p className="text-muted-foreground">{t('no_result_id_provided')}</p>
                    <Link to={backTo}>
                        <Button variant="outline">{backLabel}</Button>
                    </Link>
                </div>
                <ReviewStyles />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="ts-shell min-h-screen p-6">
                <div className="ts-page space-y-4">
                    <Skeleton className="h-20 w-full rounded-lg" />
                    <div className="ts-main-grid">
                        <Skeleton className="h-96 w-full rounded-lg" />
                        <Skeleton className="h-96 w-full rounded-lg" />
                    </div>
                </div>
                <ReviewStyles />
            </div>
        );
    }

    if (isError || !item) {
        return (
            <div className="ts-shell min-h-screen p-6">
                <div className="ts-page space-y-6">
                    <p className="text-destructive font-medium">
                        {error instanceof Error ? error.message : t('failed_to_load_history')}
                    </p>
                    <Link to={backTo}>
                        <Button variant="outline">{backLabel}</Button>
                    </Link>
                </div>
                <ReviewStyles />
            </div>
        );
    }

    const { test, passed, startedAt, duration } = item;
    const flatAnswers = (item.answers as AnswerItem[]) || [];
    const totalQuestions = flatAnswers.length;

    const resultCategory = resultCategoryKey(test?.category, item.scaledReport?.standard);
    // A full IELTS mock also holds Writing and Speaking: they are graded as bands, not as right or wrong answers,
    // so the answer map and the mistake list count only the objective questions. `reviewIndexes` points back into the review.
    const reviewIndexes = flatAnswers.flatMap((a, i) =>
        item.scaledReport && (a.questionType === 'OPEN_ENDED' || a.questionType === 'SPEAKING') ? [] : [i],
    );
    const countedAnswers = reviewIndexes.map((i) => flatAnswers[i]);
    const categoryData = buildCategoryResult(
        countedAnswers.map((a) => ({
            questionId: a.questionId,
            sectionId: a.sectionId,
            sectionTitle: a.sectionTitle,
            isCorrect: a.isCorrect,
            answered: hasAnswer(a.answer),
            itemsCorrect: a.itemsCorrect,
            itemsTotal: a.itemsTotal,
            gradingPending: a.gradingPending,
            timeSpent: a.timeSpent,
        })),
    );
    const analysisAnswers: AnalysisAnswer[] = countedAnswers.map((a) => ({
        questionType: a.questionType,
        questionText: a.questionText,
        isCorrect: a.isCorrect,
        answered: hasAnswer(a.answer),
        itemsCorrect: a.itemsCorrect,
        itemsTotal: a.itemsTotal,
        gradingPending: a.gradingPending,
        answerText: answerText(a.answer),
        correctAnswerText: item.correctAnswersHidden || !hasAnswer(a.correctAnswer) ? undefined : answerText(a.correctAnswer),
        explanation: a.explanation,
        sectionTitle: a.sectionTitle,
        timeSpent: a.timeSpent,
        flagged: a.flagged,
    }));
    const selectCounted = (n: number) => {
        goToQuestion(reviewIndexes[n - 1] ?? n - 1);
        setTimeout(() => document.getElementById(`review-q-${(reviewIndexes[n - 1] ?? n - 1) + 1}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };
    const isFullDtm = (test?.subjectConfigs?.length ?? 0) >= 3;
    const dtm = resultCategory === 'dtm' ? dtmPoints(
        flatAnswers.map((a) => ({ ...a, answered: hasAnswer(a.answer) })),
        test?.subjectConfigs,
    ) : null;
    const goalNumber = Number(goalLevel);
    const goalTarget = (resultCategory === 'ielts' || resultCategory === 'sat') && Number.isFinite(goalNumber) && goalNumber > 0 ? goalNumber : null;

    const report = item.scaledReport;
    // A band still being graded has nothing to compare yet; Listening/Reading parts compare by share correct.
    const hasComparableScore =
        !report || report.standard === 'SAT' || report.overall !== null || report.scope?.skill === 'LISTENING' || report.scope?.skill === 'READING';
    const delta = hasComparableScore
        ? attemptDelta(
              {
                  band: report?.standard === 'IELTS' ? report.overall : null,
                  total: report?.standard === 'SAT' ? report.total : null,
                  percentage: item.percentage,
              },
              item.previousAttempt,
          )
        : null;

    const historyCard = (className: string) => (
        <AttemptHistory
            category={resultCategory}
            current={{ percentage: item.percentage, band: report?.standard === 'IELTS' ? report.overall : null, total: report?.standard === 'SAT' ? report.total : null }}
            history={hasComparableScore ? (item.attemptHistory ?? []) : []}
            platform={item.platformAverage}
            dtm={resultCategory === 'dtm' && dtm && !isFullDtm ? { items: categoryData.total, max: dtm.max } : null}
            className={className}
        />
    );

    const showWeakTopics = shouldShowWeakTopics({
        hasScaledReport: Boolean(item.scaledReport),
        isPortal,
        studyReviewLoading,
        studyReviewJudged: studyReview?.facts.judged ?? 0,
    });

    const goToQuestion = (idx: number) => {
        if (idx >= 0 && idx < flatAnswers.length) setCurrentIndex(idx);
    };

    return (
        <ExamInsightsProvider value={insightsUnlock.state}>
        <div className="ts ts-shell min-h-screen">
            <div className="ts-page">
                <ResultPageHeader
                    backTo={backTo}
                    backLabel={backLabel}
                    title={test?.title || t('assessment_complete')}
                    meta={[startedAt ? formatDate(startedAt) : null, duration ? formatDuration(duration) : null, item.attemptNumber ? t('result.view.attempt_n', { n: item.attemptNumber }) : null].filter(Boolean).join(' · ')}
                    actions={
                        <>
                            {/* Learners share their own band or score; staff reading a result do not get the button. */}
                            {isPortal && item.scaledReport && <ShareScoreButton report={item.scaledReport} testTitle={test?.title || ''} />}
                            {totalQuestions > 0 && (
                                <a
                                    href="#answer-review"
                                    className="inline-flex items-center rounded-xl bg-[#10182B] px-4 py-2.5 text-[13.5px] font-bold text-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]"
                                >
                                    {t('result.view.review_answers')}
                                </a>
                            )}
                        </>
                    }
                />

                {item.scaledReport ? (
                    <>
                    <ScaledReportCard
                        report={item.scaledReport}
                        className="mb-4"
                        onRefresh={() => refetch()}
                        refreshing={isFetching}
                        target={goalTarget}
                        delta={delta}
                        afterHero={
                            <>
                                <InsightsUnlockBanner
                                    analysesRemaining={insightsUnlock.analysesRemaining}
                                    analysisLimit={insightsUnlock.analysisLimit}
                                    aiGradingPending={item.insights?.aiGradingPending === true}
                                    className="my-0"
                                />
                                {item.insights && totalQuestions > 0 && (
                                    <InsightsOpenBanner
                                        analysesRemaining={insightsUnlock.analysesRemaining}
                                        analysisLimit={insightsUnlock.analysisLimit}
                                        reviewHref="#answer-review"
                                    />
                                )}
                            </>
                        }
                    />
                    {historyCard('mb-4')}
                    {countedAnswers.length > 0 && (
                        <SkillAnalysis
                            category={resultCategory}
                            data={categoryData}
                            answers={analysisAnswers}
                            locked={insightsUnlock.state.locked}
                            partsTitle={t(resultCategory === 'sat' ? 'result.view.by_module' : 'result.view.by_part')}
                            onSelectQuestion={selectCounted}
                            questionTypes={item.analysis?.questionTypes}
                            questionTypesLocked={item.analysis?.questionTypesLocked}
                            sectionTiming={item.analysis?.sectionTiming}
                            sectionTimingLocked={item.analysis?.sectionTimingLocked}
                            className="mb-4"
                        />
                    )}
                    </>
                ) : (
                    <div className="mb-4">
                        <CategoryResultHero
                            category={resultCategory}
                            data={{ ...categoryData, answeredSeconds: categoryData.answeredSeconds || duration || 0 }}
                            levelLabel={categoryEstimate?.scoreDisplay && resultCategory !== 'dtm' ? categoryEstimate.scoreDisplay : null}
                            passed={passed}
                            dtm={resultCategory === 'dtm' ? dtm : null}
                            subjectTitle={resultCategory === 'dtm' && !isFullDtm ? test?.title : undefined}
                            goal={categoryEstimate?.goalComparison ? { target: categoryEstimate.goalComparison.userTarget } : null}
                            delta={delta}
                            ball={resultCategory === 'milliy' && !flatAnswers.some((a) => a.gradingPending) ? testBall(item.score, item.maxScore) : null}
                        />
                        <InsightsUnlockBanner
                            analysesRemaining={insightsUnlock.analysesRemaining}
                            analysisLimit={insightsUnlock.analysisLimit}
                            aiGradingPending={item.insights?.aiGradingPending === true}
                        />
                        {historyCard('mt-4')}
                        {totalQuestions > 0 && (
                            <ResultInsights
                                className="mt-4 lg:mt-5"
                                category={resultCategory}
                                data={categoryData}
                                onSelectQuestion={selectCounted}
                            />
                        )}
                        {totalQuestions > 0 && (
                            <SkillAnalysis
                                category={resultCategory}
                                data={categoryData}
                                answers={analysisAnswers}
                                locked={false}
                                partsTitle=""
                                showParts={false}
                                showMap={false}
                                onSelectQuestion={selectCounted}
                            />
                        )}
                    </div>
                )}

                {/* Candidates only: staff open the same page from /app/results and see no coaching. */}
                {isPortal && <SessionReview key={id} attemptId={id} />}

                {showWeakTopics && <WeakTopics topics={weak} className="my-4" />}

                {/* Renders nothing for anyone who is not the attempt's owner, such as staff reading a candidate's result. */}
                {id && item && item.status !== 'in_progress' && <FeedbackCard attemptId={id} />}

                <div id="answer-review" className="mt-6 scroll-mt-4">
                    <AnswerReviewList
                        answers={flatAnswers}
                        attemptId={id}
                        correctAnswersHidden={item.correctAnswersHidden}
                        focusIndex={currentIndex}
                        onFocus={setCurrentIndex}
                        accent={RESULT_ACCENTS[resultCategory].fg}
                    />
                </div>
            </div>
            <ReviewStyles />
        </div>
        </ExamInsightsProvider>
    );
};

const ReviewStyles: React.FC = () => (
    <style>{`
.ts-shell {
    --ts-primary: #171717;
    --ts-primary-fg: #FFFFFF;
    --ts-primary-50: #F4F4F5;
    --ts-primary-200: #D4D4D8;
    --ts-primary-600: #000000;
    --ts-ring: rgba(23, 23, 23, 0.16);
    --ts-bg: #FFFFFF;
    --ts-card-bg: #FFFFFF;
    --ts-gray-100: #F3F4F6;
    --ts-gray-200: #E5E7EB;
    --ts-gray-300: #D1D5DB;
    --ts-gray-400: #9CA3AF;
    --ts-gray-500: #6B7280;
    --ts-gray-600: #4B5563;
    --ts-gray-700: #374151;
    --ts-gray-900: #111827;
    --ts-success-bg: #F0FDF4;
    --ts-success-border: #BBF7D0;
    --ts-success-text: #15803D;
    --ts-success-strong: #16A34A;
    --ts-danger-bg: #FEF2F2;
    --ts-danger-border: #FECACA;
    --ts-danger-text: #B91C1C;
    --ts-danger-strong: #DC2626;
    background: var(--ts-bg);
    font-family: 'Inter', system-ui, sans-serif;
    color: var(--ts-gray-900);
}
.dark .ts-shell {
    --ts-primary: #FAFAFA;
    --ts-primary-fg: #0A0A0A;
    --ts-primary-50: rgba(250, 250, 250, 0.10);
    --ts-primary-200: rgba(250, 250, 250, 0.32);
    --ts-primary-600: #E4E4E7;
    --ts-ring: rgba(250, 250, 250, 0.22);
    --ts-bg: hsl(var(--background));
    --ts-card-bg: hsl(var(--card));
    --ts-gray-100: hsl(var(--muted));
    --ts-gray-200: hsl(var(--border));
    --ts-gray-300: hsl(220 13% 35%);
    --ts-gray-400: hsl(220 9% 55%);
    --ts-gray-500: hsl(var(--muted-foreground));
    --ts-gray-600: hsl(220 9% 70%);
    --ts-gray-700: hsl(220 9% 80%);
    --ts-gray-900: hsl(var(--foreground));
    --ts-success-bg: rgba(34, 197, 94, 0.14);
    --ts-success-border: rgba(34, 197, 94, 0.4);
    --ts-success-text: #4ADE80;
    --ts-success-strong: #22C55E;
    --ts-danger-bg: rgba(239, 68, 68, 0.14);
    --ts-danger-border: rgba(239, 68, 68, 0.4);
    --ts-danger-text: #FCA5A5;
    --ts-danger-strong: #EF4444;
}
.ts h1, .ts h2, .ts h3, .ts h4 { margin: 0; }
.ts-page {
    max-width: 1280px; margin: 0 auto; padding: 28px;
    display: flex; flex-direction: column; gap: 18px;
}
.ts-card {
    background: var(--ts-card-bg);
    border: 1px solid #E6E9F2;
    border-radius: 22px;
}
.dark .ts-card { border-color: var(--ts-gray-200); }
.ts-eyebrow {
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--ts-gray-500);
}
.ts-topbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; gap: 16px; flex-wrap: wrap;
}
.ts-title-row { display: flex; align-items: center; gap: 12px; min-width: 0; }
.ts-title-text { min-width: 0; }
.ts-title { font-size: 16px; font-weight: 700; letter-spacing: -0.02em; }
.ts-meta { font-size: 12px; color: var(--ts-gray-500); margin-top: 2px; }
.ts-topbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ts-back-btn {
    width: 32px; height: 32px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 8px;
    color: var(--ts-gray-700);
    transition: background .15s ease;
    flex-shrink: 0;
}
.ts-back-btn:hover { background: var(--ts-gray-100); }
.ts-logo {
    width: 32px; height: 32px; border-radius: 8px;
    background: var(--ts-primary); color: var(--ts-primary-fg);
    display: inline-flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.ts-score-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 999px;
    font-size: 13px; font-weight: 700;
    font-variant-numeric: tabular-nums;
}
.ts-score-chip-pass {
    background: var(--ts-success-bg); color: var(--ts-success-text); border: 1px solid var(--ts-success-border);
}
.ts-score-chip-fail {
    background: var(--ts-danger-bg); color: var(--ts-danger-text); border: 1px solid var(--ts-danger-border);
}
.ts-score-chip-neutral {
    background: var(--ts-primary-50); color: var(--ts-primary); border: 1px solid var(--ts-primary-200);
}
.ts-category-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 999px;
    font-size: 13px; font-weight: 700;
}
.ts-category-chip-gold {
    background: #fef3c7; color: #b45309; border: 1px solid #fde68a;
}
.ts-category-chip-emerald {
    background: #d1fae5; color: #047857; border: 1px solid #a7f3d0;
}
.ts-category-chip-blue {
    background: #dbeafe; color: #1d4ed8; border: 1px solid #bfdbfe;
}
.ts-category-chip-purple {
    background: #f3e8ff; color: #6d28d9; border: 1px solid #e9d5ff;
}
.ts-category-chip-amber {
    background: #fef3c7; color: #b45309; border: 1px solid #fde68a;
}
.ts-category-chip-rose {
    background: #ffe4e6; color: #be123c; border: 1px solid #fecdd3;
}
.ts-category-chip-slate {
    background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;
}
.dark .ts-category-chip-gold {
    background: rgba(245, 158, 11, 0.15); color: #fbbf24; border-color: rgba(245, 158, 11, 0.4);
}
.dark .ts-category-chip-emerald {
    background: rgba(16, 185, 129, 0.15); color: #34d399; border-color: rgba(16, 185, 129, 0.4);
}
.dark .ts-category-chip-blue {
    background: rgba(59, 130, 246, 0.15); color: #60a5fa; border-color: rgba(59, 130, 246, 0.4);
}
.dark .ts-category-chip-purple {
    background: rgba(139, 92, 246, 0.15); color: #c084fc; border-color: rgba(139, 92, 246, 0.4);
}
.dark .ts-category-chip-amber {
    background: rgba(245, 158, 11, 0.15); color: #fbbf24; border-color: rgba(245, 158, 11, 0.4);
}
.dark .ts-category-chip-rose {
    background: rgba(244, 63, 94, 0.15); color: #fb7185; border-color: rgba(244, 63, 94, 0.4);
}
.dark .ts-category-chip-slate {
    background: rgba(148, 163, 184, 0.15); color: #cbd5e1; border-color: rgba(148, 163, 184, 0.4);
}
.ts-goal-chip-met {
    background: #d1fae5 !important; color: #047857 !important; border: 1px solid #a7f3d0 !important;
}
.dark .ts-goal-chip-met {
    background: rgba(16, 185, 129, 0.15) !important; color: #34d399 !important; border-color: rgba(16, 185, 129, 0.4) !important;
}
.ts-goal-chip-missed {
    background: #fef3c7 !important; color: #b45309 !important; border: 1px solid #fde68a !important;
}
.dark .ts-goal-chip-missed {
    background: rgba(245, 158, 11, 0.15) !important; color: #fbbf24 !important; border-color: rgba(245, 158, 11, 0.4) !important;
}
.ts-meta-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 10px; border-radius: 999px;
    background: var(--ts-primary-50); color: var(--ts-primary);
    border: 1px solid var(--ts-primary-200);
    font-size: 12px; font-weight: 600;
}
.ts-meta-chip-muted {
    background: var(--ts-gray-100); color: var(--ts-gray-600);
    border-color: var(--ts-gray-200);
}
.ts-progress-row {
    display: flex; align-items: center; gap: 14px;
    padding: 12px 18px;
    border-top: 1px solid var(--ts-gray-100);
}
.ts-progress-bar {
    flex: 1; height: 6px;
    background: var(--ts-gray-100);
    border-radius: 999px; overflow: hidden;
}
.ts-progress-bar > div { height: 100%; border-radius: 999px; transition: width .3s ease; }
.ts-progress-fill-pass {
    background: var(--ts-success-strong);
}
.ts-progress-fill-fail {
    background: var(--ts-danger-strong);
}
.ts-progress-fill-neutral {
    background: var(--ts-primary);
}
.ts-progress-label {
    font-size: 12px; font-weight: 500; color: var(--ts-gray-600);
    font-variant-numeric: tabular-nums;
}

.ts-main-grid {
    display: grid; grid-template-columns: minmax(0, 1fr) 320px;
    gap: 18px; align-items: start;
}
.ts-reading-grid { grid-template-columns: minmax(0, 1fr); }
.ts-reading-grid > aside { display: none; }
.ts-passage-card { width: 100%; max-width: 100%; }
@media (max-width: 1023px) {
    .ts-main-grid { grid-template-columns: minmax(0, 1fr); }
    .ts-side { position: static !important; }
}
@media (max-width: 640px) {
    .ts-page { padding: 16px; gap: 14px; }
    .ts-topbar { padding: 12px 14px; }
    .ts-progress-row { padding: 10px 14px; }
    .ts-q-card { padding: 20px 18px; }
    .ts-q-prompt { font-size: 17px; }
    .ts-q-header { margin-bottom: 16px; }
    .ts-q-footer { margin-top: 20px; padding-top: 16px; gap: 8px; }
    .ts-btn { padding: 8px 12px; font-size: 12px; }
    .ts-btn-secondary { padding: 7px 10px; }
    .ts-autosave { display: none; }
}

.ts-q-card { padding: 28px 32px; }
.ts-q-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 16px; margin-bottom: 20px;
}
.ts-q-tags { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ts-q-num {
    font-size: 13px; font-weight: 700;
    color: var(--ts-gray-500); margin-bottom: 8px;
}
.ts-q-badge {
    font-size: 11px; font-weight: 700;
    padding: 4px 9px; border-radius: 999px;
    background: var(--ts-primary-50); color: var(--ts-primary);
    letter-spacing: 0.02em;
}
.ts-q-type {
    font-size: 11px; font-weight: 600;
    color: var(--ts-gray-500);
    text-transform: uppercase; letter-spacing: 0.06em;
}
.ts-q-pts { font-size: 12px; color: var(--ts-gray-500); font-weight: 500; }
.ts-q-prompt {
    font-size: 19px; font-weight: 600; line-height: 1.45;
    letter-spacing: -0.01em; color: var(--ts-gray-900);
    margin-bottom: 24px;
}
.ts-q-options { display: flex; flex-direction: column; gap: 8px; }

.ts-result-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 10px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
    flex-shrink: 0;
}
.ts-result-badge-correct {
    background: var(--ts-success-bg); color: var(--ts-success-text); border: 1px solid var(--ts-success-border);
}
.ts-result-badge-incorrect {
    background: var(--ts-danger-bg); color: var(--ts-danger-text); border: 1px solid var(--ts-danger-border);
}
.ts-result-badge-skipped {
    background: var(--ts-gray-100); color: var(--ts-gray-600); border: 1px solid var(--ts-gray-200);
}

.ts-review-opt {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 16px; border-radius: 10px;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
}
.ts-review-opt-correct {
    border-color: var(--ts-success-border); background: var(--ts-success-bg);
}
.ts-review-opt-incorrect {
    border-color: var(--ts-danger-border); background: var(--ts-danger-bg);
}
.ts-review-opt-mark {
    width: 20px; height: 20px; border-radius: 999px;
    border: 2px solid var(--ts-gray-300);
    flex-shrink: 0; margin-top: 1px;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--ts-card-bg);
}
.ts-review-opt-mark-square { border-radius: 6px; }
.ts-review-opt-mark-correct {
    border-color: var(--ts-success-strong); background: var(--ts-success-strong);
}
.ts-review-opt-mark-incorrect {
    border-color: var(--ts-danger-strong); background: var(--ts-danger-strong);
}
.ts-review-opt-mark-fill {
    width: 7px; height: 7px; background: #fff; border-radius: 999px;
}
.ts-opt-letter {
    font-size: 11px; font-weight: 700;
    color: var(--ts-gray-500); margin-right: 4px; margin-top: 2px;
    min-width: 12px;
}
.ts-review-opt-correct .ts-opt-letter { color: var(--ts-success-text); }
.ts-review-opt-incorrect .ts-opt-letter { color: var(--ts-danger-text); }
.ts-opt-text {
    font-size: 14px; line-height: 1.55; color: var(--ts-gray-700);
}
.ts-review-opt-correct .ts-opt-text { color: var(--ts-success-text); font-weight: 500; }
.ts-review-opt-incorrect .ts-opt-text { color: var(--ts-danger-text); font-weight: 500; }
.ts-review-opt-icon { margin-left: auto; flex-shrink: 0; }

.ts-review-fill {
    padding: 14px 16px; border-radius: 10px; border: 1px solid var(--ts-gray-200);
    background: var(--ts-card-bg);
}
.ts-review-fill-empty { background: var(--ts-gray-100); }
.ts-review-fill-correct { background: var(--ts-success-bg); border-color: var(--ts-success-border); }
.ts-review-fill-incorrect { background: var(--ts-danger-bg); border-color: var(--ts-danger-border); }
.ts-review-fill-label {
    font-size: 11px; font-weight: 600;
    color: var(--ts-gray-500);
    text-transform: uppercase; letter-spacing: 0.04em;
    margin-bottom: 4px;
}
.ts-review-fill-value { font-size: 14px; font-weight: 500; color: var(--ts-gray-900); }
.ts-review-fill-empty .ts-review-fill-value {
    color: var(--ts-gray-500); font-style: italic;
}
.ts-review-fill-correct .ts-review-fill-value { color: var(--ts-success-text); }
.ts-review-fill-incorrect .ts-review-fill-value { color: var(--ts-danger-text); }

.ts-review-match-row {
    display: grid; grid-template-columns: 1fr auto 1fr;
    gap: 8px; align-items: center;
}
.ts-review-match-key {
    padding: 10px 12px; border-radius: 8px;
    background: var(--ts-gray-100); color: var(--ts-gray-900);
    font-size: 14px; font-weight: 500;
}
.ts-review-match-eq { color: var(--ts-gray-400); font-size: 12px; }
.ts-review-match-val {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
    font-size: 14px; font-weight: 500;
}
.ts-review-match-val-empty {
    background: var(--ts-gray-100); color: var(--ts-gray-500); font-style: italic;
}
.ts-review-match-val-correct {
    background: var(--ts-success-bg); border-color: var(--ts-success-border); color: var(--ts-success-text);
}
.ts-review-match-val-incorrect {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border); color: var(--ts-danger-text);
}
.ts-review-match-correction {
    display: block; font-size: 12px; font-weight: 400;
    color: var(--ts-success-text); margin-top: 2px;
}

.ts-q-footer {
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px;
    margin-top: 28px; padding-top: 20px;
    border-top: 1px solid var(--ts-gray-100);
}
.ts-autosave {
    flex: 1; min-width: 0; text-align: center;
    font-size: 12px; color: var(--ts-gray-500);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    font-variant-numeric: tabular-nums;
}
.ts-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 16px; border-radius: 8px;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: background .15s ease;
    border: 1px solid transparent;
}
.ts-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ts-btn-primary { background: var(--ts-primary); color: var(--ts-primary-fg); }
.ts-btn-primary:hover:not(:disabled) { background: var(--ts-primary-600); }
.ts-btn-secondary {
    background: var(--ts-card-bg); color: var(--ts-gray-900);
    border-color: var(--ts-gray-200); padding: 8px 14px;
}
.ts-btn-secondary:hover:not(:disabled) {
    background: var(--ts-gray-100); border-color: var(--ts-gray-300);
}

.ts-side {
    display: flex; flex-direction: column; gap: 14px;
    position: sticky; top: 18px;
}
.ts-nav-card { padding: 0; }
.ts-nav-head {
    padding: 14px 16px;
    border-bottom: 1px solid var(--ts-gray-100);
    display: flex; justify-content: space-between; align-items: center;
}
.ts-nav-count {
    font-size: 11px; color: var(--ts-gray-500);
    font-weight: 600; font-variant-numeric: tabular-nums;
}
.ts-subj-block { padding: 14px 16px; }
.ts-subj-block + .ts-subj-block { border-top: 1px solid var(--ts-gray-100); }
.ts-subj-toggle {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; background: transparent; border: 0;
    padding: 0; cursor: pointer; font-family: inherit; text-align: left;
    margin-bottom: 10px;
}
.ts-subj-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.ts-subj-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.ts-subj-dot { width: 8px; height: 8px; border-radius: 999px; flex-shrink: 0; }
.ts-subj-name {
    font-size: 13px; font-weight: 700;
    color: var(--ts-gray-900); letter-spacing: -0.01em;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ts-subj-count {
    font-size: 11px; color: var(--ts-gray-500);
    font-weight: 500; font-variant-numeric: tabular-nums;
}
.ts-subj-chevron {
    color: var(--ts-gray-400);
    transition: transform .2s ease, color .2s ease;
    display: inline-flex; align-items: center; justify-content: center;
}
.ts-subj-toggle:hover .ts-subj-chevron { color: var(--ts-gray-600); }
.ts-subj-progress {
    height: 4px; background: var(--ts-gray-100);
    border-radius: 999px; overflow: hidden;
    margin-bottom: 12px;
}
.ts-subj-progress > div { height: 100%; border-radius: 999px; transition: width .3s ease; }
.ts-subj-grid-wrap {
    overflow: hidden;
    transition: max-height .25s ease, opacity .2s ease, margin-top .2s ease;
}
.ts-subj-grid-wrap[data-open='false'] { max-height: 0; opacity: 0; margin-top: 0; }
.ts-subj-grid-wrap[data-open='true'] { max-height: 1200px; opacity: 1; margin-top: 0; }

.ts-nav-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(34px, 1fr));
    gap: 6px; padding: 4px;
}
.ts-nav-tile {
    width: 100%; aspect-ratio: 1 / 1;
    min-width: 0;
    border-radius: 8px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 600;
    cursor: pointer; position: relative;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
    color: var(--ts-gray-700);
    transition: all .12s ease;
    font-variant-numeric: tabular-nums;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.ts-nav-tile:hover { border-color: var(--ts-gray-300); }
.ts-nav-tile-correct {
    background: var(--ts-success-bg); border-color: var(--ts-success-border); color: var(--ts-success-text);
}
.ts-nav-tile-incorrect {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border); color: var(--ts-danger-text);
}
.ts-nav-tile-skipped {
    background: var(--ts-card-bg); border-color: var(--ts-gray-200); color: var(--ts-gray-400);
}
.ts-nav-tile-current {
    background: var(--ts-primary); border-color: var(--ts-primary);
    color: var(--ts-primary-fg); box-shadow: 0 0 0 3px var(--ts-ring);
}
.ts-legend-row {
    padding: 12px 16px;
    border-top: 1px solid var(--ts-gray-100);
}
.ts-legend { display: flex; gap: 12px; flex-wrap: wrap; }
.ts-legend-item {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; color: var(--ts-gray-500); font-weight: 500;
}
.ts-legend-swatch {
    width: 12px; height: 12px; border-radius: 4px;
    border: 1px solid var(--ts-gray-200);
}
.ts-legend-swatch-success {
    background: var(--ts-success-bg); border-color: var(--ts-success-border);
}
.ts-legend-swatch-danger {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border);
}
.ts-legend-swatch-empty { background: var(--ts-card-bg); }
`}</style>
);

export default TestResults;
